import React, { useState, useEffect, useRef } from 'react';
import Select from 'react-select';
import DashboardLayout from './DashboardLayout';
import ConfirmationModal from './ConfirmationModal';
import { adminApi } from '../lib/api';
import { useAuth } from '../lib/AuthContext';
import { useSuccessAlert, useErrorAlert } from './AlertSystem';
import { extractErrorMessage } from '../lib/utils';
import './AdminCustomMonitoringManagement.css';

interface CustomMonitoringRequest {
    id: string;
    name: string;
    email: string;
    company?: string;
    monitoringType: string;
    description: string;
    budgetRange?: string;
    timeline?: string;
    status: 'NEW' | 'REVIEWING' | 'QUOTED' | 'APPROVED' | 'COMPLETED' | 'REJECTED';
    adminNotes?: string;
    requirements?: any;
    createdAt: string;
    updatedAt: string;
    user?: {
        id: string;
        name: string;
        email: string;
    };
}

interface PaginationInfo {
    currentPage: number;
    totalPages: number;
    totalItems: number;
    itemsPerPage: number;
    hasNext: boolean;
    hasPrev: boolean;
}

interface UpdateRequestData {
    status: 'NEW' | 'REVIEWING' | 'QUOTED' | 'APPROVED' | 'COMPLETED' | 'REJECTED';
    adminNotes: string;
}

interface CustomMonitoringStats {
    total: number;
    byStatus: {
        new: number;
        reviewing: number;
        quoted: number;
        approved: number;
        completed: number;
        rejected: number;
    };
    byBudget: Array<{
        range: string;
        count: number;
    }>;
    metrics: {
        recentRequests: number;
        completionRate: number;
        approvalRate: number;
    };
}

const AdminCustomMonitoringManagement: React.FC = () => {
    const { hasPermission, isSuperAdmin } = useAuth();
    const [requests, setRequests] = useState<CustomMonitoringRequest[]>([]);
    const [loading, setLoading] = useState(true);
    const [tableLoading, setTableLoading] = useState(false);
    const [searchQuery, setSearchQuery] = useState('');
    const [debouncedSearch, setDebouncedSearch] = useState('');
    const [statusFilter, setStatusFilter] = useState('all');
    const [selectedRequest, setSelectedRequest] = useState<CustomMonitoringRequest | null>(null);
    const [isEditModalOpen, setIsEditModalOpen] = useState(false);
    const [isDetailsModalOpen, setIsDetailsModalOpen] = useState(false);
    const [currentPage, setCurrentPage] = useState(1);
    const [pagination, setPagination] = useState<PaginationInfo | null>(null);
    const [updateRequestData, setUpdateRequestData] = useState<UpdateRequestData>({
        status: 'NEW',
        adminNotes: ''
    });
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
    const [requestToDelete, setRequestToDelete] = useState<CustomMonitoringRequest | null>(null);
    const [stats, setStats] = useState<CustomMonitoringStats | null>(null);

    // Alert hooks
    const showSuccess = useSuccessAlert();
    const showError = useErrorAlert();

    // Ref for search input to maintain focus
    const searchInputRef = useRef<HTMLInputElement>(null);
    const wasSearchingRef = useRef(false);

    // Enhanced search input handler that preserves focus
    const handleSearchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const newValue = e.target.value;
        setSearchQuery(newValue);

        // Ensure the input stays focused during typing
        if (searchInputRef.current) {
            // Store cursor position
            const cursorPosition = searchInputRef.current.selectionStart;

            // Use setTimeout to restore focus after any potential re-renders
            setTimeout(() => {
                if (searchInputRef.current && document.activeElement !== searchInputRef.current) {
                    searchInputRef.current.focus();
                    // Restore cursor position
                    if (cursorPosition !== null) {
                        searchInputRef.current.setSelectionRange(cursorPosition, cursorPosition);
                    }
                }
            }, 1);
        }
    };

    const requestsPerPage = 10;

    // Single function to load requests - NO USEEFFECT DEPENDENCIES 
    const loadRequests = async () => {
        try {
            // Show loading only for initial load, use tableLoading for searches
            if (requests.length === 0) {
                setLoading(true);
            } else {
                setTableLoading(true);
            }

            const response = await adminApi.getCustomMonitoringRequests({
                page: currentPage,
                limit: requestsPerPage,
                search: debouncedSearch.trim() || undefined,
                status: statusFilter !== 'all' ? statusFilter : undefined
            });

            if (response.success && response.data) {
                setRequests(response.data.requests || []);
                setPagination(response.data.pagination);
            } else {
                const errorMsg = extractErrorMessage(response);

                // Handle authentication errors
                if (response.data?.redirect === '/login') {
                    window.location.href = '/login';
                    return;
                }

                // Only show error alert for non-rate-limited errors
                if (!response.message?.includes('Too many')) {
                    showError(errorMsg, 'Error Loading Requests');
                }

                console.error('Fetch requests error:', errorMsg);
            }
        } catch (err) {
            const errorMsg = 'Failed to connect to server';
            showError(errorMsg, 'Connection Error');
            console.error('Fetch requests error:', err);
        } finally {
            setLoading(false);
            setTableLoading(false);
        }
    };

    // Load stats function
    const loadStats = async () => {
        try {
            const response = await adminApi.getCustomMonitoringStats();
            if (response.success && response.data) {
                setStats(response.data);
            }
        } catch (err) {
            console.error('Failed to load stats:', err);
        }
    };

    // Refetch function for CRUD operations
    const refetchRequests = async () => {
        await Promise.all([loadRequests(), loadStats()]);
    };

    // ONLY ONE USEEFFECT - Initial load only
    useEffect(() => {
        Promise.all([loadRequests(), loadStats()]);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []); // Empty dependencies - only runs once

    // Debounce search input - updates debouncedSearch after user stops typing
    useEffect(() => {
        const timer = setTimeout(() => {
            setDebouncedSearch(searchQuery);
        }, 300); // Short debounce for search input

        return () => clearTimeout(timer);
    }, [searchQuery]);

    // Manual trigger when debouncedSearch or statusFilter changes
    useEffect(() => {
        if (currentPage === 1) {
            loadRequests(); // Reload with current filters
        } else {
            setCurrentPage(1); // This will trigger loadRequests via page effect
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [debouncedSearch, statusFilter]);

    // Manual trigger when page changes
    useEffect(() => {
        if (currentPage > 1) { // Don't run on initial mount
            loadRequests();
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [currentPage]);

    // Focus management for search input during transitions
    useEffect(() => {
        // Store if user was searching when results become empty
        if (requests.length === 0 && debouncedSearch.length > 0) {
            wasSearchingRef.current = true;
        }

        // Restore focus if user was searching and results come back
        if (wasSearchingRef.current && requests.length > 0 && debouncedSearch.length > 0) {
            // Small delay to ensure DOM is updated
            setTimeout(() => {
                if (searchInputRef.current && document.activeElement !== searchInputRef.current) {
                    searchInputRef.current.focus();
                }
            }, 10);
            wasSearchingRef.current = false;
        }

        // Reset flag if search is cleared
        if (debouncedSearch.length === 0) {
            wasSearchingRef.current = false;
        }
    }, [requests.length, debouncedSearch]);

    // Additional focus preservation for input changes
    useEffect(() => {
        // Preserve focus during any re-renders if user is actively typing
        const preserveFocus = () => {
            if (searchQuery.length > 0 && searchInputRef.current &&
                document.activeElement !== searchInputRef.current &&
                !document.activeElement?.closest('.custom-select')) {
                // Only restore focus if user isn't interacting with the select dropdown
                searchInputRef.current.focus();
            }
        };

        // Small delay to handle re-renders
        const timer = setTimeout(preserveFocus, 50);
        return () => clearTimeout(timer);
    }, [requests, searchQuery]); // Re-run when requests change

    const formatDate = (dateString: string) => {
        if (!dateString) return 'Never';

        const date = new Date(dateString);
        // Ensure we're displaying in the user's local timezone
        return date.toLocaleDateString('en-US', {
            year: 'numeric',
            month: 'short',
            day: 'numeric',
            hour: '2-digit',
            minute: '2-digit',
            timeZoneName: 'short'
        });
    };

    const formatTimeAgo = (dateString: string) => {
        if (!dateString) return 'Never';

        const date = new Date(dateString);
        const now = new Date();

        // Calculate difference in UTC to avoid timezone issues
        const diffInMs = now.getTime() - date.getTime();
        const diffInMinutes = Math.floor(diffInMs / (1000 * 60));
        const diffInHours = Math.floor(diffInMs / (1000 * 60 * 60));
        const diffInDays = Math.floor(diffInMs / (1000 * 60 * 60 * 24));

        if (diffInMinutes < 1) {
            return 'Just now';
        } else if (diffInMinutes < 60) {
            return `${diffInMinutes}m ago`;
        } else if (diffInHours < 24) {
            return `${diffInHours}h ago`;
        } else if (diffInDays < 7) {
            return `${diffInDays}d ago`;
        } else {
            return formatDate(dateString);
        }
    };

    const getStatusColor = (status: string) => {
        switch (status) {
            case 'NEW': return '#3b82f6';
            case 'REVIEWING': return '#f59e0b';
            case 'QUOTED': return '#8b5cf6';
            case 'APPROVED': return '#10b981';
            case 'COMPLETED': return '#059669';
            case 'REJECTED': return '#ef4444';
            default: return '#6b7280';
        }
    };

    const handleViewDetails = (request: CustomMonitoringRequest) => {
        setSelectedRequest(request);
        setIsDetailsModalOpen(true);
    };

    const handleEditRequest = (request: CustomMonitoringRequest) => {
        setSelectedRequest(request);
        setUpdateRequestData({
            status: request.status,
            adminNotes: request.adminNotes || ''
        });
        setIsEditModalOpen(true);
    };

    const handleDeleteRequest = async (request: CustomMonitoringRequest) => {
        setRequestToDelete(request);
        setIsDeleteModalOpen(true);
    };

    const confirmDeleteRequest = async () => {
        if (!requestToDelete) return;

        try {
            setIsSubmitting(true);
            const response = await adminApi.deleteCustomMonitoringRequest(requestToDelete.id);

            if (response.success) {
                await refetchRequests();
                showSuccess(`${requestToDelete.name}'s request has been deleted successfully`, 'Request Deleted');
            } else {
                showError(response.error || 'Failed to delete request', 'Delete Failed');
            }
        } catch (err) {
            console.error('Delete request error:', err);
            showError('Network error occurred while deleting request', 'Delete Failed');
        } finally {
            setIsSubmitting(false);
            setIsDeleteModalOpen(false);
            setRequestToDelete(null);
        }
    };

    const handleSaveRequest = async () => {
        if (!selectedRequest) return;

        try {
            setIsSubmitting(true);
            const response = await adminApi.updateCustomMonitoringRequest(selectedRequest.id, updateRequestData);

            if (response.success) {
                await refetchRequests();
                setIsEditModalOpen(false);
                showSuccess(`${selectedRequest.name}'s request has been updated successfully`, 'Request Updated');
            } else {
                const errorMsg = extractErrorMessage(response);
                showError(errorMsg, 'Update Failed');
            }
        } catch (err) {
            console.error('Update request error:', err);
            showError('Network error occurred while updating request', 'Update Failed');
        } finally {
            setIsSubmitting(false);
        }
    };

    const statusOptions = [
        { value: 'all', label: 'All Statuses' },
        { value: 'NEW', label: 'New' },
        { value: 'REVIEWING', label: 'Reviewing' },
        { value: 'QUOTED', label: 'Quoted' },
        { value: 'APPROVED', label: 'Approved' },
        { value: 'COMPLETED', label: 'Completed' },
        { value: 'REJECTED', label: 'Rejected' }
    ];

    const requestStatusOptions = [
        { value: 'NEW', label: 'New' },
        { value: 'REVIEWING', label: 'Reviewing' },
        { value: 'QUOTED', label: 'Quoted' },
        { value: 'APPROVED', label: 'Approved' },
        { value: 'COMPLETED', label: 'Completed' },
        { value: 'REJECTED', label: 'Rejected' }
    ];

    // Custom styles for react-select
    const selectStyles = {
        control: (provided: any, state: any) => ({
            ...provided,
            background: 'rgba(26, 26, 39, 0.8)',
            border: `1px solid ${state.isFocused ? 'var(--primary-purple)' : 'rgba(148, 163, 184, 0.2)'}`,
            borderRadius: '8px',
            padding: '2px 4px',
            color: 'var(--text-primary)',
            fontSize: '0.875rem',
            fontFamily: "'Inter', 'Chivo', sans-serif",
            minHeight: '40px',
            boxShadow: state.isFocused ? '0 0 0 2px rgba(99, 102, 241, 0.15)' : 'none',
            cursor: 'pointer',
            '&:hover': {
                border: `1px solid ${state.isFocused ? 'var(--primary-purple)' : 'rgba(99, 102, 241, 0.4)'}`
            }
        }),
        menu: (provided: any) => ({
            ...provided,
            background: 'rgba(26, 26, 39, 0.95)',
            border: '1px solid rgba(99, 102, 241, 0.3)',
            borderRadius: '8px',
            boxShadow: '0 4px 20px rgba(0, 0, 0, 0.3)',
            zIndex: 99999999,
            backdropFilter: 'blur(12px)',
            WebkitBackdropFilter: 'blur(12px)'
        }),
        option: (provided: any, state: any) => ({
            ...provided,
            background: state.isSelected
                ? 'rgba(99, 102, 241, 0.2)'
                : state.isFocused
                    ? 'rgba(255, 255, 255, 0.08)'
                    : 'transparent',
            color: state.isSelected ? 'var(--primary-purple)' : 'var(--text-primary)',
            fontSize: '0.875rem',
            fontFamily: "'Inter', 'Chivo', sans-serif",
            padding: '10px 14px',
            cursor: 'pointer',
            '&:hover': {
                background: state.isSelected ? 'rgba(99, 102, 241, 0.3)' : 'rgba(255, 255, 255, 0.08)'
            }
        }),
        singleValue: (provided: any) => ({
            ...provided,
            color: 'var(--text-primary)',
            fontSize: '0.875rem',
            fontFamily: "'Inter', 'Chivo', sans-serif"
        }),
        placeholder: (provided: any) => ({
            ...provided,
            color: 'var(--text-muted)',
            fontSize: '0.875rem',
            fontFamily: "'Inter', 'Chivo', sans-serif"
        }),
        input: (provided: any) => ({
            ...provided,
            color: 'var(--text-primary)',
            fontSize: '0.875rem',
            fontFamily: "'Inter', 'Chivo', sans-serif"
        }),
        dropdownIndicator: (provided: any) => ({
            ...provided,
            color: 'var(--text-muted)',
            padding: '6px',
            '&:hover': {
                color: 'var(--text-primary)'
            }
        }),
        indicatorSeparator: () => ({
            display: 'none'
        })
    };

    if (loading && requests.length === 0) {
        return (
            <div className="admin-custom-monitoring-loading">
                <div className="admin-custom-monitoring-loading-spinner">
                    <i className="ri-loader-4-line"></i>
                </div>
                <div className="admin-custom-monitoring-loading-text">Loading custom monitoring requests...</div>
            </div>
        );
    }

    return (
        <DashboardLayout title="Custom Monitoring Management">
            <div className="admin-custom-monitoring">
                {/* Header */}
                <div className="admin-custom-monitoring-header">
                    <div className="admin-custom-monitoring-header-content">
                        <h1 className="admin-custom-monitoring-title">Custom Monitoring Requests</h1>
                        <p className="admin-custom-monitoring-subtitle">
                            Manage custom security monitoring solution requests from clients
                        </p>
                    </div>
                    <div className="admin-custom-monitoring-stats">
                        <div className="admin-custom-monitoring-stat">
                            <div className="admin-custom-monitoring-stat-value">{stats?.total || 0}</div>
                            <div className="admin-custom-monitoring-stat-label">Total Requests</div>
                        </div>
                        <div className="admin-custom-monitoring-stat">
                            <div className="admin-custom-monitoring-stat-value">{stats?.byStatus?.completed || 0}</div>
                            <div className="admin-custom-monitoring-stat-label">Completed</div>
                        </div>
                        <div className="admin-custom-monitoring-stat">
                            <div className="admin-custom-monitoring-stat-value">{stats?.metrics?.completionRate || 0}%</div>
                            <div className="admin-custom-monitoring-stat-label">Completion Rate</div>
                        </div>
                    </div>
                </div>

                {/* Filters */}
                <div className="admin-custom-monitoring-filters">
                    <div className="admin-custom-monitoring-search">
                        <i className="ri-search-line"></i>
                        <input
                            key="search-input" // Stable key to prevent re-creation
                            ref={searchInputRef}
                            type="text"
                            placeholder="Search requests by name, email, company, or monitoring type..."
                            value={searchQuery}
                            onChange={handleSearchChange}
                            className="admin-custom-monitoring-search-input"
                            autoComplete="off"
                        />
                    </div>
                    <div className="admin-custom-monitoring-filter-group">
                        <Select
                            options={statusOptions}
                            value={statusOptions.find(option => option.value === statusFilter)}
                            onChange={(option) => setStatusFilter(option?.value || 'all')}
                            placeholder="Filter by status"
                            styles={selectStyles}
                            isSearchable={false}
                            className="admin-custom-monitoring-status-filter"
                            isDisabled={loading}
                        />
                    </div>
                </div>

                {/* Loading indicator for filters */}
                {loading && requests.length > 0 && (
                    <div className="admin-custom-monitoring-loading-indicator">
                        <i className="ri-loader-4-line"></i>
                        <span>Updating results...</span>
                    </div>
                )}

                {/* Requests Table */}
                <div className="admin-custom-monitoring-table-container" style={{ position: 'relative' }}>
                    {tableLoading && (
                        <div style={{
                            position: 'absolute',
                            top: 0,
                            left: 0,
                            right: 0,
                            bottom: 0,
                            background: 'rgba(0, 0, 0, 0.1)',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            zIndex: 10,
                            borderRadius: '0.75rem'
                        }}>
                            <div style={{
                                background: 'rgba(23, 23, 28, 0.9)',
                                padding: '1rem 1.5rem',
                                borderRadius: '0.5rem',
                                display: 'flex',
                                alignItems: 'center',
                                gap: '0.5rem',
                                color: 'var(--text-secondary)',
                                fontSize: '0.875rem'
                            }}>
                                <i className="ri-loader-4-line" style={{ animation: 'spin 1s linear infinite' }}></i>
                                Searching...
                            </div>
                        </div>
                    )}
                    {requests.length === 0 && !loading && !tableLoading ? (
                        <div className="admin-custom-monitoring-empty">
                            <i className="ri-eye-line"></i>
                            <h3>No requests found</h3>
                            <p>Try adjusting your search or filter criteria</p>
                        </div>
                    ) : (
                        <div className="admin-custom-monitoring-table" style={{ opacity: tableLoading ? 0.7 : 1 }}>
                            <div className="admin-custom-monitoring-table-header">
                                <div className="admin-custom-monitoring-table-cell">Client</div>
                                <div className="admin-custom-monitoring-table-cell">Company</div>
                                <div className="admin-custom-monitoring-table-cell">Monitoring Type</div>
                                <div className="admin-custom-monitoring-table-cell">Budget</div>
                                <div className="admin-custom-monitoring-table-cell">Status</div>
                                <div className="admin-custom-monitoring-table-cell">Created</div>
                                <div className="admin-custom-monitoring-table-cell">Actions</div>
                            </div>
                            <div className="admin-custom-monitoring-table-body">
                                {requests.map((request) => (
                                    <div key={request.id} className="admin-custom-monitoring-table-row">
                                        <div className="admin-custom-monitoring-table-cell">
                                            <div className="admin-request-info">
                                                <div className="admin-request-avatar">
                                                    <i className="ri-user-fill"></i>
                                                </div>
                                                <div className="admin-request-details">
                                                    <div className="admin-request-name">{request.name}</div>
                                                    <div className="admin-request-email">{request.email}</div>
                                                </div>
                                            </div>
                                        </div>
                                        <div className="admin-custom-monitoring-table-cell">
                                            <span className="admin-request-company">
                                                {request.company || 'Not specified'}
                                            </span>
                                        </div>
                                        <div className="admin-custom-monitoring-table-cell">
                                            <span className="admin-request-monitoring-type">
                                                {request.monitoringType}
                                            </span>
                                        </div>
                                        <div className="admin-custom-monitoring-table-cell">
                                            <span className="admin-request-budget">
                                                {request.budgetRange || 'Not specified'}
                                            </span>
                                        </div>
                                        <div className="admin-custom-monitoring-table-cell">
                                            <span
                                                className="admin-request-status-badge"
                                                style={{ backgroundColor: `${getStatusColor(request.status)}20`, color: getStatusColor(request.status) }}
                                            >
                                                {request.status}
                                            </span>
                                        </div>
                                        <div className="admin-custom-monitoring-table-cell">
                                            <span className="admin-request-created-date">
                                                {formatTimeAgo(request.createdAt)}
                                            </span>
                                        </div>
                                        <div className="admin-custom-monitoring-table-cell">
                                            <div className="admin-request-actions">
                                                <button
                                                    className="admin-request-action-btn"
                                                    onClick={() => handleViewDetails(request)}
                                                    title="View details"
                                                    disabled={isSubmitting}
                                                >
                                                    <i className="ri-eye-line"></i>
                                                </button>
                                                {(hasPermission('custom_monitoring_edit') || isSuperAdmin()) && (
                                                    <button
                                                        className="admin-request-action-btn"
                                                        onClick={() => handleEditRequest(request)}
                                                        title="Edit request"
                                                        disabled={isSubmitting}
                                                    >
                                                        <i className="ri-edit-line"></i>
                                                    </button>
                                                )}
                                                {(hasPermission('custom_monitoring_delete') || isSuperAdmin()) && (
                                                    <button
                                                        className="admin-request-action-btn admin-request-delete-btn"
                                                        onClick={() => handleDeleteRequest(request)}
                                                        title="Delete request"
                                                        disabled={isSubmitting}
                                                    >
                                                        <i className="ri-delete-bin-line"></i>
                                                    </button>
                                                )}
                                            </div>
                                        </div>
                                    </div>
                                ))}
                            </div>
                        </div>
                    )}
                </div>

                {/* Pagination */}
                {pagination && pagination.totalPages > 1 && (
                    <div className="admin-custom-monitoring-pagination">
                        <button
                            className="admin-custom-monitoring-pagination-btn"
                            onClick={() => setCurrentPage(currentPage - 1)}
                            disabled={currentPage === 1 || loading}
                        >
                            <i className="ri-arrow-left-line"></i>
                            Previous
                        </button>
                        <div className="admin-custom-monitoring-pagination-info">
                            Page {currentPage} of {pagination.totalPages} ({pagination.totalItems} requests)
                        </div>
                        <button
                            className="admin-custom-monitoring-pagination-btn"
                            onClick={() => setCurrentPage(currentPage + 1)}
                            disabled={currentPage === pagination.totalPages || loading}
                        >
                            Next
                            <i className="ri-arrow-right-line"></i>
                        </button>
                    </div>
                )}

                {/* Details Modal */}
                {isDetailsModalOpen && selectedRequest && (
                    <div className="admin-custom-monitoring-modal-overlay" onClick={() => setIsDetailsModalOpen(false)}>
                        <div className="admin-custom-monitoring-modal admin-custom-monitoring-details-modal" onClick={(e) => e.stopPropagation()}>
                            <div className="admin-custom-monitoring-modal-header">
                                <h3>Request Details</h3>
                                <button onClick={() => setIsDetailsModalOpen(false)}>
                                    <i className="ri-close-line"></i>
                                </button>
                            </div>
                            <div className="admin-custom-monitoring-modal-content">
                                <div className="admin-custom-monitoring-details">
                                    <div className="admin-custom-monitoring-details-section">
                                        <h4>Basic Information</h4>
                                        <div className="admin-custom-monitoring-details-grid">
                                            <div className="admin-custom-monitoring-detail-item">
                                                <label>Contact Person:</label>
                                                <span>{selectedRequest.name}</span>
                                            </div>
                                            <div className="admin-custom-monitoring-detail-item">
                                                <label>Email:</label>
                                                <span>{selectedRequest.email}</span>
                                            </div>
                                            <div className="admin-custom-monitoring-detail-item">
                                                <label>Company:</label>
                                                <span>{selectedRequest.company || 'Not specified'}</span>
                                            </div>
                                            <div className="admin-custom-monitoring-detail-item">
                                                <label>Budget Range:</label>
                                                <span>{selectedRequest.budgetRange || 'Not specified'}</span>
                                            </div>
                                            <div className="admin-custom-monitoring-detail-item">
                                                <label>Timeline:</label>
                                                <span>{selectedRequest.timeline || 'Not specified'}</span>
                                            </div>
                                            <div className="admin-custom-monitoring-detail-item">
                                                <label>Status:</label>
                                                <span
                                                    className="admin-request-status-badge"
                                                    style={{ backgroundColor: `${getStatusColor(selectedRequest.status)}20`, color: getStatusColor(selectedRequest.status) }}
                                                >
                                                    {selectedRequest.status}
                                                </span>
                                            </div>
                                        </div>
                                    </div>

                                    <div className="admin-custom-monitoring-details-section">
                                        <h4>Monitoring Requirements</h4>
                                        <div className="admin-custom-monitoring-detail-item">
                                            <label>Monitoring Type:</label>
                                            <span>{selectedRequest.monitoringType}</span>
                                        </div>
                                        <div className="admin-custom-monitoring-detail-item">
                                            <label>Description:</label>
                                            <div className="admin-custom-monitoring-description">
                                                {selectedRequest.description}
                                            </div>
                                        </div>
                                    </div>

                                    {selectedRequest.requirements && (
                                        <div className="admin-custom-monitoring-details-section">
                                            <h4>Detailed Requirements</h4>
                                            <pre className="admin-custom-monitoring-requirements">
                                                {JSON.stringify(selectedRequest.requirements, null, 2)}
                                            </pre>
                                        </div>
                                    )}

                                    {selectedRequest.adminNotes && (
                                        <div className="admin-custom-monitoring-details-section">
                                            <h4>Admin Notes</h4>
                                            <div className="admin-custom-monitoring-admin-notes">
                                                {selectedRequest.adminNotes}
                                            </div>
                                        </div>
                                    )}

                                    <div className="admin-custom-monitoring-details-section">
                                        <h4>Timestamps</h4>
                                        <div className="admin-custom-monitoring-details-grid">
                                            <div className="admin-custom-monitoring-detail-item">
                                                <label>Created:</label>
                                                <span>{formatDate(selectedRequest.createdAt)}</span>
                                            </div>
                                            <div className="admin-custom-monitoring-detail-item">
                                                <label>Last Updated:</label>
                                                <span>{formatDate(selectedRequest.updatedAt)}</span>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                )}

                {/* Edit Modal */}
                {isEditModalOpen && selectedRequest && (
                    <div className="admin-custom-monitoring-modal-overlay" onClick={() => setIsEditModalOpen(false)}>
                        <div className="admin-custom-monitoring-modal" onClick={(e) => e.stopPropagation()}>
                            <div className="admin-custom-monitoring-modal-header">
                                <h3>Update Request</h3>
                                <button onClick={() => setIsEditModalOpen(false)}>
                                    <i className="ri-close-line"></i>
                                </button>
                            </div>
                            <div className="admin-custom-monitoring-modal-content">
                                <div className="admin-custom-monitoring-form">
                                    <div className="admin-custom-monitoring-form-group">
                                        <label>Status</label>
                                        <Select
                                            options={requestStatusOptions}
                                            value={requestStatusOptions.find(option => option.value === updateRequestData.status)}
                                            onChange={(option) => setUpdateRequestData({ ...updateRequestData, status: option?.value as 'NEW' | 'REVIEWING' | 'QUOTED' | 'APPROVED' | 'COMPLETED' | 'REJECTED' || 'NEW' })}
                                            styles={selectStyles}
                                            isSearchable={false}
                                            placeholder="Select status"
                                        />
                                    </div>
                                    <div className="admin-custom-monitoring-form-group">
                                        <label>Admin Notes</label>
                                        <textarea
                                            value={updateRequestData.adminNotes}
                                            onChange={(e) => setUpdateRequestData({ ...updateRequestData, adminNotes: e.target.value })}
                                            placeholder="Add internal notes about this request..."
                                            rows={4}
                                        />
                                    </div>
                                    <div className="admin-custom-monitoring-form-actions">
                                        <button
                                            className="admin-custom-monitoring-btn admin-custom-monitoring-btn-secondary"
                                            onClick={() => setIsEditModalOpen(false)}
                                            disabled={isSubmitting}
                                        >
                                            Cancel
                                        </button>
                                        <button
                                            className="admin-custom-monitoring-btn admin-custom-monitoring-btn-primary"
                                            onClick={handleSaveRequest}
                                            disabled={isSubmitting}
                                        >
                                            {isSubmitting ? 'Saving...' : 'Save Changes'}
                                        </button>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                )}

                {/* Delete Confirmation Modal */}
                <ConfirmationModal
                    isOpen={isDeleteModalOpen}
                    onClose={() => setIsDeleteModalOpen(false)}
                    onConfirm={confirmDeleteRequest}
                    title="Delete Request"
                    message="Are you sure you want to delete this custom monitoring request? This action cannot be undone and will remove all associated data."
                    confirmText="Delete Request"
                    type="danger"
                    isLoading={isSubmitting}
                >
                    {requestToDelete && (
                        <div className="admin-custom-monitoring-delete-modal-request">
                            <div className="admin-custom-monitoring-delete-modal-request-avatar">
                                <i className="ri-eye-line"></i>
                            </div>
                            <div className="admin-custom-monitoring-delete-modal-request-info">
                                <h4>{requestToDelete.name}</h4>
                                <p>{requestToDelete.email}</p>
                                <div className="admin-custom-monitoring-delete-modal-request-meta">
                                    <span className="admin-custom-monitoring-delete-modal-request-company">
                                        {requestToDelete.company || 'No company'}
                                    </span>
                                    <span className="admin-custom-monitoring-delete-modal-request-status"
                                        style={{ color: getStatusColor(requestToDelete.status) }}>
                                        {requestToDelete.status}
                                    </span>
                                </div>
                            </div>
                        </div>
                    )}
                </ConfirmationModal>
            </div>
        </DashboardLayout>
    );
};

export default AdminCustomMonitoringManagement;
